import React, { PropsWithChildren, useCallback } from 'react';
import { Drawer } from 'vaul';

export const DrawerOverlay = ({ className }: { className?: string }) => (
    <Drawer.Overlay className={className} />
);

interface IDrawerContentProps {
    className?: string;
    onClose: () => void;
    setContentRef: React.Dispatch<React.SetStateAction<HTMLDivElement>>;
}

const handlePointerDownOutside = (
    _event: CustomEvent<{ originalEvent: PointerEvent }>,
    onClose: () => void
) => {
    // onPointerDownOutside fires when clicking outside the drawer
    // We should always allow the drawer to close in this case
    onClose();
};

export const DrawerContent = ({
    children,
    className,
    onClose,
    setContentRef
}: PropsWithChildren<IDrawerContentProps>) => {
    // Ensures form inputs can gain focus when clicked inside drawer
    // Stops propagation for activation elements (like HBMoneyInput) so their onClick handlers fire
    // All other elements work normally - their onClick handlers fire and can close the drawer
    const handlePointerDown = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement;

            const formInputTags = ['INPUT', 'TEXTAREA', 'SELECT'];
            const isButtonOrLink =
                target.tagName === 'BUTTON' || target.tagName === 'A';

            if (formInputTags.includes(target.tagName)) {
                // For form inputs, prevent default and stop propagation to ensure focus works
                e.preventDefault();
                e.stopPropagation();
                requestAnimationFrame(() => {
                    if (target instanceof HTMLElement && 'focus' in target) {
                        target.focus();
                    }
                });
            } else if (!isButtonOrLink && target.tagName === 'DIV') {
                // For HBMoneyInput specifically (div with role="button" and tabIndex)
                // Check if it's the currency container by testId pattern
                const testId = target.getAttribute('data-testid');
                const isMoneyInputContainer =
                    testId && testId.includes('money-input-currency');

                if (isMoneyInputContainer) {
                    // Prevent default and stop propagation to prevent drawer from interfering
                    // Then manually trigger click so onClick handler fires
                    e.preventDefault();
                    e.stopPropagation();
                    requestAnimationFrame(() => {
                        target.click();
                    });
                }
                // Don't interfere with other divs (like HBClickable used in drawer options)
            }
            // Buttons/links work normally - their onClick handlers fire and can close the drawer
        },
        []
    );

    return (
        <Drawer.Content
            className={className}
            data-testid="hb-drawer--container"
            onEscapeKeyDown={onClose}
            onPointerDownOutside={e => handlePointerDownOutside(e, onClose)}
            onPointerDownCapture={handlePointerDown}
            ref={setContentRef}
        >
            {children}
        </Drawer.Content>
    );
};
